import React, { useEffect, useState } from 'react';
import {
  fetchPracticeSessions,
  createPracticeSession,
  deletePracticeSession,
  startPracticeSession,
  updatePracticeProgress,
  completePracticeSession,
  restartPracticeSession,
  fetchLanguages,
  fetchSchoolYears,
  fetchLectures,
  fetchWords,
  updateWordStats
} from '../utils/contentApi';
import Notification from '../components/Notification';
import { useNotification } from '../hooks/useNotification';

function PracticeConfigModal({ session, onClose, onStart }) {
  const [translationDirection, setTranslationDirection] = useState('native-to-foreign'); // 'native-to-foreign' or 'foreign-to-native'
  const [quizType, setQuizType] = useState('word-by-word'); // 'word-by-word', '5-min-quiz', 'repeat-lower-50'

  const language = session.lectures[0]?.schoolYear?.language;
  const nativeLanguage = language?.nativeLanguage;
  const foreignLanguage = language?.foreignLanguage;

  const handleStart = () => {
    onStart({ translationDirection, quizType });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-3/4 max-w-2xl p-8">
        <h2 className="text-2xl font-bold mb-6">Configure Practice Session #{session.sessionNumber}</h2>

        {/* Translation Direction */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Translation Direction
          </label>
          <div className="space-y-2">
            <button
              onClick={() => setTranslationDirection('native-to-foreign')}
              className={`w-full flex items-center justify-center gap-3 px-4 py-4 border-2 rounded-lg transition-all ${
                translationDirection === 'native-to-foreign'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-indigo-300 bg-white'
              }`}
            >
              <span className={`fi fi-${nativeLanguage?.iso?.toLowerCase()}`} style={{ fontSize: '2em' }}></span>
              <span className="text-2xl">→</span>
              <span className={`fi fi-${foreignLanguage?.iso?.toLowerCase()}`} style={{ fontSize: '2em' }}></span>
              <span className={`flex-1 text-left font-medium ml-3 ${translationDirection === 'native-to-foreign' ? 'text-indigo-900' : 'text-gray-700'}`}>
                {nativeLanguage?.name} to {foreignLanguage?.name}
              </span>
              {translationDirection === 'native-to-foreign' && (
                <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </button>
            <button
              onClick={() => setTranslationDirection('foreign-to-native')}
              className={`w-full flex items-center justify-center gap-3 px-4 py-4 border-2 rounded-lg transition-all ${
                translationDirection === 'foreign-to-native'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-indigo-300 bg-white'
              }`}
            >
              <span className={`fi fi-${foreignLanguage?.iso?.toLowerCase()}`} style={{ fontSize: '2em' }}></span>
              <span className="text-2xl">→</span>
              <span className={`fi fi-${nativeLanguage?.iso?.toLowerCase()}`} style={{ fontSize: '2em' }}></span>
              <span className={`flex-1 text-left font-medium ml-3 ${translationDirection === 'foreign-to-native' ? 'text-indigo-900' : 'text-gray-700'}`}>
                {foreignLanguage?.name} to {nativeLanguage?.name}
              </span>
              {translationDirection === 'foreign-to-native' && (
                <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Quiz Type */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Quiz Type
          </label>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => setQuizType('word-by-word')}
              className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-all ${
                quizType === 'word-by-word'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-indigo-300 bg-white'
              }`}
            >
              <img
                src="/wordbyword.jpg"
                alt="Word by Word"
                className="w-24 h-24 object-cover rounded mb-2"
              />
              <span className={`text-sm font-medium text-center ${quizType === 'word-by-word' ? 'text-indigo-900' : 'text-gray-700'}`}>
                Word by Word
              </span>
            </button>
            <button
              onClick={() => setQuizType('5-min-quiz')}
              className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-all ${
                quizType === '5-min-quiz'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-indigo-300 bg-white'
              }`}
            >
              <img
                src="/5minquiz.jpg"
                alt="5 Min Quiz"
                className="w-24 h-24 object-cover rounded mb-2"
              />
              <span className={`text-sm font-medium text-center ${quizType === '5-min-quiz' ? 'text-indigo-900' : 'text-gray-700'}`}>
                5 Min Quiz
              </span>
            </button>
            <button
              onClick={() => setQuizType('repeat-lower-50')}
              className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-all ${
                quizType === 'repeat-lower-50'
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-indigo-300 bg-white'
              }`}
            >
              <img
                src="/lower50.jpg"
                alt="Repeat Lower 50%"
                className="w-24 h-24 object-cover rounded mb-2"
              />
              <span className={`text-sm font-medium text-center ${quizType === 'repeat-lower-50' ? 'text-indigo-900' : 'text-gray-700'}`}>
                Repeat Lower 50%
              </span>
            </button>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Start Practice
          </button>
        </div>
      </div>
    </div>
  );
}

function PracticeModal({ session, onClose, onComplete, notify, config }) {
  const [words, setWords] = useState([]);
  const [coveredWordIds, setCoveredWordIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showError, setShowError] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [firstAttempt, setFirstAttempt] = useState(true);
  const [stats, setStats] = useState({
    firstAttemptCorrect: 0,
    totalWords: 0
  });
  const [loading, setLoading] = useState(true);
  const [practicing, setPracticing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [wordTimeRemaining, setWordTimeRemaining] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [mistakes, setMistakes] = useState([]);
  const [showMistakesReport, setShowMistakesReport] = useState(false);
  const [answerOptions, setAnswerOptions] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  useEffect(() => {
    const loadWords = async () => {
      try {
        setLoading(true);
        const allWords = [];

        // Fetch words from all lectures in the session
        for (const lecture of session.lectures) {
          const lectureWords = await fetchWords(lecture._id);
          allWords.push(...lectureWords);
        }

        // Apply quiz type filter
        let filteredWords = allWords;
        if (config.quizType === 'repeat-lower-50') {
          // Sort by success rate (correctCount / challengedCount)
          filteredWords = allWords
            .map(word => ({
              ...word,
              successRate: word.challengedCount > 0 ? (word.correctCount / word.challengedCount) : 0
            }))
            .sort((a, b) => a.successRate - b.successRate)
            .slice(0, Math.ceil(allWords.length * 0.5)); // Take lower 50%
        }

        // If resuming, filter out already covered words
        let wordsToStudy;
        if (session.status === 'In Progress' && session.coveredWords && session.coveredWords.length > 0) {
          const coveredIds = session.coveredWords.map(w => typeof w === 'string' ? w : w._id);
          setCoveredWordIds(coveredIds);
          wordsToStudy = filteredWords.filter(word => !coveredIds.includes(word._id));

          // Restore previous stats
          setStats({
            firstAttemptCorrect: session.firstAttemptCorrect || 0,
            totalWords: filteredWords.length
          });
        } else {
          // New session - shuffle words randomly
          wordsToStudy = filteredWords.sort(() => Math.random() - 0.5);
          setStats({ firstAttemptCorrect: 0, totalWords: filteredWords.length });
          setCoveredWordIds([]);
        }

        setWords(wordsToStudy);

        // Set timer for 5-min quiz
        if (config.quizType === '5-min-quiz') {
          setTimeRemaining(5 * 60); // 5 minutes in seconds
        }
      } catch (err) {
        console.error('Failed to load words:', err);
        notify.error('Failed to load words for practice');
      } finally {
        setLoading(false);
      }
    };

    loadWords();
  }, [session, config]); // eslint-disable-line react-hooks/exhaustive-deps

  // Timer effect for 5-min quiz (session timer)
  useEffect(() => {
    if (config.quizType === '5-min-quiz' && timeRemaining !== null && timeRemaining > 0 && !isPaused) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSessionTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeRemaining, config.quizType, isPaused]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-word timer for 5-min quiz (8 seconds max per word)
  useEffect(() => {
    if (config.quizType === '5-min-quiz' && wordTimeRemaining !== null && wordTimeRemaining > 0 && !isPaused) {
      const timer = setInterval(() => {
        setWordTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleWordTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [wordTimeRemaining, config.quizType, isPaused, currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate answer options for 5-min quiz when word changes
  useEffect(() => {
    if (config.quizType === '5-min-quiz' && words.length > 0 && currentIndex < words.length) {
      const word = words[currentIndex];
      if (!word) return;

      const correctAnswer = config.translationDirection === 'native-to-foreign'
        ? word.foreignWord
        : word.nativeWord;

      // Get all possible wrong answers from the word pool
      const wrongAnswers = words
        .filter(w => w._id !== word._id)
        .map(w => config.translationDirection === 'native-to-foreign' ? w.foreignWord : w.nativeWord)
        .filter(answer => answer !== correctAnswer); // Ensure no duplicates

      // Randomly select 2 wrong answers (or as many as available if less than 2)
      const shuffledWrong = wrongAnswers.sort(() => Math.random() - 0.5).slice(0, 2);

      // Combine correct answer with wrong answers and shuffle
      const options = [correctAnswer, ...shuffledWrong].sort(() => Math.random() - 0.5);

      setAnswerOptions(options);
      setWordTimeRemaining(8); // Reset to 8 seconds for new word
    }
  }, [currentIndex, config.quizType, words, config.translationDirection]);

  const handleSessionTimeUp = () => {
    // 5 minutes expired - end session and show mistakes report
    setShowMistakesReport(true);
  };

  const handleWordTimeout = () => {
    // 8 seconds expired - mark as wrong and move to next word
    handleQuizAnswer(null, true); // Pass null answer and timeout flag
  };

  const currentWord = words[currentIndex];
  const language = session.lectures[0]?.schoolYear?.language;

  // Determine question and answer based on translation direction
  const questionWord = config.translationDirection === 'native-to-foreign'
    ? currentWord?.nativeWord
    : currentWord?.foreignWord;
  const correctAnswer = config.translationDirection === 'native-to-foreign'
    ? currentWord?.foreignWord
    : currentWord?.nativeWord;
  const questionLanguage = config.translationDirection === 'native-to-foreign'
    ? language?.nativeLanguage
    : language?.foreignLanguage;
  const answerLanguage = config.translationDirection === 'native-to-foreign'
    ? language?.foreignLanguage
    : language?.nativeLanguage;

  const handleQuizAnswer = async (selectedAnswer, isTimeout = false) => {
    if (!currentWord) return;

    const correctAnswer = config.translationDirection === 'native-to-foreign'
      ? currentWord.foreignWord
      : currentWord.nativeWord;

    const isAnswerCorrect = !isTimeout && selectedAnswer === correctAnswer;

    // Update word statistics
    await updateWordStats(currentWord._id, true, isAnswerCorrect);

    // Show feedback
    if (isAnswerCorrect) {
      setFeedbackMessage({ type: 'success', text: 'Correct!' });
      setStats(prev => ({ ...prev, firstAttemptCorrect: prev.firstAttemptCorrect + 1 }));
    } else {
      setFeedbackMessage({ type: 'error', text: isTimeout ? 'Time\'s up!' : 'Incorrect!' });
      // Track mistake
      setMistakes(prev => [...prev, {
        question: config.translationDirection === 'native-to-foreign' ? currentWord.nativeWord : currentWord.foreignWord,
        correctAnswer: correctAnswer,
        userAnswer: isTimeout ? 'No answer (timeout)' : selectedAnswer
      }]);
    }

    // Clear feedback after 1 second and move to next word
    setTimeout(() => {
      setFeedbackMessage(null);
      moveToNextWordQuiz(isAnswerCorrect);
    }, 1000);
  };

  const handleNext = async () => {
    if (!currentWord) return;

    const trimmedAnswer = userAnswer.trim();
    const trimmedCorrectAnswer = correctAnswer.trim();
    const isAnswerCorrect = trimmedAnswer.toLowerCase() === trimmedCorrectAnswer.toLowerCase();

    if (!isCorrect && !showError) {
      // First submission - check answer
      setPracticing(true);

      // Update word statistics
      await updateWordStats(currentWord._id, true, isAnswerCorrect);

      if (isAnswerCorrect) {
        // Correct answer
        setIsCorrect(true);
        const wasFirstAttempt = firstAttempt;
        if (wasFirstAttempt) {
          setStats(prev => ({ ...prev, firstAttemptCorrect: prev.firstAttemptCorrect + 1 }));
        }

        // Move to next word after short delay, passing the increment info
        setTimeout(() => {
          moveToNextWord(wasFirstAttempt);
        }, 500);
      } else {
        // Wrong answer - show error
        setShowError(true);
        setFirstAttempt(false);
      }
      setPracticing(false);
    } else if (showError) {
      // Second submission after error - just move to next word
      moveToNextWord(false);
    }
  };

  const moveToNextWordQuiz = async (wasCorrect) => {
    // Add current word to covered words
    const newCoveredWordIds = [...coveredWordIds, currentWord._id];
    setCoveredWordIds(newCoveredWordIds);

    // Save progress to backend
    try {
      await updatePracticeProgress(
        session._id,
        newCoveredWordIds,
        stats.firstAttemptCorrect,
        stats.totalWords
      );
    } catch (err) {
      console.error('Failed to save progress:', err);
    }

    if (currentIndex < words.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // All words completed - show mistakes report
      setShowMistakesReport(true);
    }
  };

  const moveToNextWord = async (wasFirstAttemptCorrect = false) => {
    // Add current word to covered words
    const newCoveredWordIds = [...coveredWordIds, currentWord._id];
    setCoveredWordIds(newCoveredWordIds);

    // Calculate the current firstAttemptCorrect count accounting for the latest answer
    const currentFirstAttemptCorrect = stats.firstAttemptCorrect + (wasFirstAttemptCorrect ? 1 : 0);

    // Save progress to backend
    try {
      await updatePracticeProgress(
        session._id,
        newCoveredWordIds,
        currentFirstAttemptCorrect,
        stats.totalWords
      );
    } catch (err) {
      console.error('Failed to save progress:', err);
    }

    if (currentIndex < words.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setUserAnswer('');
      setShowError(false);
      setIsCorrect(false);
      setFirstAttempt(true);
    } else {
      // All words completed
      handleComplete(newCoveredWordIds, currentFirstAttemptCorrect);
    }
  };

  const handleComplete = async (finalCoveredWords, finalFirstAttemptCorrect) => {
    try {
      // Use the passed value or fall back to the state value
      const firstAttemptCount = finalFirstAttemptCorrect !== undefined
        ? finalFirstAttemptCorrect
        : stats.firstAttemptCorrect;

      await completePracticeSession(
        session._id,
        finalCoveredWords || coveredWordIds,
        firstAttemptCount,
        stats.totalWords
      );
      const finalScore = stats.totalWords > 0
        ? Math.round((firstAttemptCount / stats.totalWords) * 100)
        : 0;
      notify.success(
        `Practice completed!\n\nFirst attempt correct: ${firstAttemptCount} / ${stats.totalWords}\nFinal Score: ${finalScore}%`,
        5000
      );
      onComplete();
      onClose();
    } catch (err) {
      console.error('Failed to complete session:', err);
      notify.error('Failed to save practice results');
    }
  };

  const handlePause = () => {
    setIsPaused(true);
  };

  const handleResume = () => {
    setIsPaused(false);
  };

  const handleFinishQuizSession = async () => {
    try {
      // Complete the quiz session with the words actually covered
      const wordsAttempted = coveredWordIds.length;
      await completePracticeSession(
        session._id,
        coveredWordIds,
        stats.firstAttemptCorrect,
        wordsAttempted
      );
      const finalScore = wordsAttempted > 0
        ? Math.round((stats.firstAttemptCorrect / wordsAttempted) * 100)
        : 0;
      notify.success(
        `Quiz completed!\n\nCorrect answers: ${stats.firstAttemptCorrect} / ${wordsAttempted}\nFinal Score: ${finalScore}%`,
        5000
      );
      onComplete();
      onClose();
    } catch (err) {
      console.error('Failed to complete quiz session:', err);
      notify.error('Failed to save quiz results');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !practicing) {
      handleNext();
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center">Loading words...</div>
        </div>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <h3 className="text-xl font-bold mb-4">No Words Found</h3>
          <p className="text-gray-600 mb-4">There are no words in the selected lectures.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Mistakes Report Modal for 5-min quiz
  if (showMistakesReport) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-3/4 max-w-3xl p-8 max-h-[80vh] overflow-y-auto">
          <h2 className="text-2xl font-bold mb-4">Quiz Complete!</h2>

          <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="text-lg font-semibold text-indigo-900 mb-2">Your Score</div>
            <div className="text-3xl font-bold text-indigo-700">
              {stats.firstAttemptCorrect} / {coveredWordIds.length} correct
            </div>
            <div className="text-sm text-indigo-600 mt-1">
              {coveredWordIds.length > 0
                ? `${Math.round((stats.firstAttemptCorrect / coveredWordIds.length) * 100)}%`
                : '0%'}
            </div>
          </div>

          {mistakes.length > 0 ? (
            <>
              <h3 className="text-xl font-bold mb-3 text-red-700">Mistakes ({mistakes.length})</h3>
              <div className="space-y-3 mb-6">
                {mistakes.map((mistake, index) => (
                  <div key={index} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-semibold text-gray-800">Question: {mistake.question}</div>
                      <div className="text-xs text-gray-500">#{index + 1}</div>
                    </div>
                    <div className="text-red-700 mb-1">
                      Your answer: <span className="font-medium">{mistake.userAnswer}</span>
                    </div>
                    <div className="text-green-700">
                      Correct answer: <span className="font-medium">{mistake.correctAnswer}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-6 bg-green-50 border border-green-200 rounded-lg mb-6 text-center">
              <div className="text-2xl font-bold text-green-700 mb-2">Perfect Score!</div>
              <div className="text-green-600">You got all answers correct!</div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleFinishQuizSession}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Finish
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Pause Screen for 5-min quiz
  if (isPaused) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Paused</h2>
          <p className="text-gray-600 mb-6">The timer is stopped. Click resume to continue.</p>
          <button
            onClick={handleResume}
            className="px-8 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-lg font-semibold"
          >
            Resume
          </button>
        </div>
      </div>
    );
  }

  // 5-min Quiz Mode UI
  if (config.quizType === '5-min-quiz') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-3/4 max-w-2xl p-8">
          {/* Timer at top */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <div className="text-sm text-blue-700 font-medium mb-1">Time Remaining</div>
            <div className="text-4xl font-bold text-blue-900">
              {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
            </div>
            <div className="text-xs text-blue-600 mt-2">
              Word {coveredWordIds.length + 1} of {stats.totalWords} •
              {wordTimeRemaining !== null && ` ${wordTimeRemaining}s left for this word`}
            </div>
          </div>

          {/* Question word in middle */}
          <div className="mb-2 text-sm text-gray-600 text-center">
            Translate from {questionLanguage?.name || 'Question'}
          </div>
          <div className="text-4xl font-bold mb-4 p-6 bg-gray-50 rounded-lg text-center">
            {questionWord}
          </div>

          {/* Word timer progress bar */}
          <div className="mb-8">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-1000 ${
                  wordTimeRemaining <= 2 ? 'bg-red-500' : wordTimeRemaining <= 4 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${(wordTimeRemaining / 8) * 100}%` }}
              />
            </div>
            <div className="text-center text-xs text-gray-500 mt-1">
              {wordTimeRemaining}s remaining
            </div>
          </div>

          {/* Answer buttons */}
          <div className="mb-6 space-y-3">
            {answerOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleQuizAnswer(option)}
                disabled={feedbackMessage !== null}
                className="w-full px-6 py-4 text-lg font-medium border-2 border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {option}
              </button>
            ))}
          </div>

          {/* Feedback message - fixed height area */}
          <div className="mb-6 h-16 flex items-center justify-center">
            {feedbackMessage && (
              <div className={`w-full p-3 rounded-lg text-center font-semibold ${
                feedbackMessage.type === 'success'
                  ? 'bg-green-100 text-green-700 border border-green-300'
                  : 'bg-red-100 text-red-700 border border-red-300'
              }`}>
                {feedbackMessage.text}
              </div>
            )}
          </div>

          {/* Bottom controls */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Correct: {stats.firstAttemptCorrect} / {coveredWordIds.length}
            </div>
            <button
              onClick={handlePause}
              className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
            >
              Pause
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Word-by-word and Repeat Lower 50% mode UI
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-3/4 max-w-2xl p-8">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl font-bold">Practice Session #{session.sessionNumber}</h2>
            <div className="text-sm text-gray-600">
              Word {coveredWordIds.length + 1} of {stats.totalWords}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all"
              style={{ width: `${(coveredWordIds.length / stats.totalWords) * 100}%` }}
            />
          </div>
        </div>

        <div className="mb-6">
          <div className="text-sm text-gray-600 mb-2">
            {questionLanguage?.name || 'Question'}
          </div>
          <div className="text-3xl font-bold mb-6 p-4 bg-gray-50 rounded-lg text-center">
            {questionWord}
          </div>

          <div className="text-sm text-gray-600 mb-2">
            Your answer ({answerLanguage?.name || 'Answer'}):
          </div>
          <input
            type="text"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isCorrect || practicing}
            className={`w-full px-4 py-3 text-lg border rounded-md focus:outline-none focus:ring-2 ${
              isCorrect
                ? 'border-green-500 bg-green-50'
                : showError
                ? 'border-red-500'
                : 'border-gray-300 focus:ring-indigo-500'
            }`}
            placeholder="Enter your answer"
            autoFocus
          />

          {showError && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
              <div className="text-red-700 font-medium mb-1">Incorrect!</div>
              <div className="text-red-600">
                Correct answer: <span className="font-bold">{correctAnswer}</span>
              </div>
              <div className="text-sm text-red-500 mt-1">Please correct your answer and try again.</div>
            </div>
          )}

          {isCorrect && (
            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 font-medium">
              Correct! Moving to next word...
            </div>
          )}
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            First Attempt Correct: {stats.firstAttemptCorrect} / {currentIndex + (isCorrect ? 1 : 0)}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={practicing}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleNext}
              disabled={practicing || (!userAnswer.trim() && !showError) || isCorrect}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {practicing ? 'Checking...' : showError ? 'Next' : 'Check'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreatePracticeSessionModal({ onClose, onSave, notify }) {
  const [languages, setLanguages] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedSchoolYears, setSelectedSchoolYears] = useState([]);
  const [selectedLectures, setSelectedLectures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [languagesData, schoolYearsData, lecturesData] = await Promise.all([
          fetchLanguages(),
          fetchSchoolYears(),
          fetchLectures()
        ]);
        setLanguages(languagesData);
        setSchoolYears(schoolYearsData);
        setLectures(lecturesData);
      } catch (err) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getAvailableSchoolYears = () => {
    if (!selectedLanguage) return [];

    // Get school years for the selected language
    return schoolYears.filter(
      sy => sy.language === selectedLanguage || (sy.language && sy.language._id === selectedLanguage)
    );
  };

  const getAvailableLectures = () => {
    if (selectedSchoolYears.length === 0) return [];

    // Get lectures for the selected school years
    return lectures.filter(lecture =>
      selectedSchoolYears.includes(lecture.schoolYear) ||
      selectedSchoolYears.includes(lecture.schoolYear?._id)
    );
  };

  const handleLanguageChange = (languageId) => {
    setSelectedLanguage(languageId);
    setSelectedSchoolYears([]);
    setSelectedLectures([]);
  };

  const handleSchoolYearToggle = (schoolYearId) => {
    setSelectedSchoolYears(prev => {
      const newSelection = prev.includes(schoolYearId)
        ? prev.filter(id => id !== schoolYearId)
        : [...prev, schoolYearId];

      // Clear lecture selection when school years change
      if (newSelection.length === 0) {
        setSelectedLectures([]);
      } else {
        // Remove lectures that are no longer in selected school years
        const availableLectureIds = lectures
          .filter(l => newSelection.includes(l.schoolYear) || newSelection.includes(l.schoolYear?._id))
          .map(l => l._id);
        setSelectedLectures(current => current.filter(id => availableLectureIds.includes(id)));
      }

      return newSelection;
    });
  };

  const handleLectureToggle = (lectureId) => {
    setSelectedLectures(prev => {
      if (prev.includes(lectureId)) {
        return prev.filter(id => id !== lectureId);
      } else {
        return [...prev, lectureId];
      }
    });
  };

  const handleSave = async () => {
    if (selectedLectures.length === 0) {
      notify.warning('Please select at least one lecture');
      return;
    }

    setSaving(true);
    try {
      await onSave(selectedLectures);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create practice session');
    } finally {
      setSaving(false);
    }
  };

  const availableSchoolYears = getAvailableSchoolYears();
  const availableLectures = getAvailableLectures();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-3/4 max-w-3xl p-6 max-h-[80vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6">Create Practice Session</h2>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <>
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            {/* Language Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Language
              </label>
              <div className="space-y-2">
                {languages.map(lang => (
                  <button
                    key={lang._id}
                    onClick={() => handleLanguageChange(lang._id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 border-2 rounded-lg transition-all ${
                      selectedLanguage === lang._id
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 hover:border-indigo-300 bg-white'
                    }`}
                  >
                    <span className={`fi fi-${lang.nativeLanguage?.iso?.toLowerCase()}`} style={{ fontSize: '1.5em' }}></span>
                    <span className="text-xl">→</span>
                    <span className={`fi fi-${lang.foreignLanguage?.iso?.toLowerCase()}`} style={{ fontSize: '1.5em' }}></span>
                    <span className={`flex-1 text-left font-medium ${selectedLanguage === lang._id ? 'text-indigo-900' : 'text-gray-700'}`}>
                      {lang.name}
                    </span>
                    {selectedLanguage === lang._id && (
                      <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* School Year Selection */}
            {selectedLanguage && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Classes ({selectedSchoolYears.length} selected)
                </label>
                {availableSchoolYears.length === 0 ? (
                  <div className="p-4 bg-gray-50 rounded text-gray-600">
                    No classes available for this language
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableSchoolYears.map(schoolYear => (
                      <button
                        key={schoolYear._id}
                        onClick={() => handleSchoolYearToggle(schoolYear._id)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          selectedSchoolYears.includes(schoolYear._id)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {schoolYear.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Lecture Selection */}
            {selectedSchoolYears.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Lectures ({selectedLectures.length} selected)
                </label>
                {availableLectures.length === 0 ? (
                  <div className="p-4 bg-gray-50 rounded text-gray-600">
                    No lectures available for the selected classes
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                    {availableLectures.map(lecture => {
                      // Find the school year name for this lecture
                      const schoolYear = schoolYears.find(sy =>
                        sy._id === lecture.schoolYear || sy._id === lecture.schoolYear?._id
                      );
                      const schoolYearName = schoolYear?.name || lecture.schoolYear?.name || '';

                      return (
                        <label
                          key={lecture._id}
                          className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLectures.includes(lecture._id)}
                            onChange={() => handleLectureToggle(lecture._id)}
                            className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="text-gray-800">{schoolYearName} - {lecture.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Buttons */}
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || selectedLectures.length === 0}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
              >
                {saving ? 'Creating...' : 'Create Practice Session'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const PracticePage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [practiceConfig, setPracticeConfig] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const { notifications, removeNotification, success, error: notifyError, warning, info } = useNotification();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPracticeSessions();
      setSessions(data);
    } catch (err) {
      console.error('Failed to load practice sessions:', err);
      setError(err.message || 'Failed to load practice sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (lectureIds) => {
    try {
      setError(null);
      await createPracticeSession(lectureIds);
      await loadSessions();
      success('Practice session created successfully!');
    } catch (err) {
      setError(err.message || 'Failed to create practice session');
      notifyError(err.message || 'Failed to create practice session');
      throw err;
    }
  };

  const handleDeleteSession = async (sessionId) => {
    setConfirmAction({
      type: 'delete',
      sessionId,
      message: 'Are you sure you want to delete this practice session?'
    });
  };

  const executeDeleteSession = async (sessionId) => {
    try {
      setError(null);
      await deletePracticeSession(sessionId);
      await loadSessions();
      success('Practice session deleted successfully!');
    } catch (err) {
      setError(err.message || 'Failed to delete practice session');
      notifyError(err.message || 'Failed to delete practice session');
    }
  };

  const handleStartPractice = async (session) => {
    try {
      setCurrentSession(session);

      // If session is already in progress, skip config modal and go directly to practice
      if (session.status === 'In Progress') {
        // Use existing configuration from the session
        setPracticeConfig({
          translationDirection: session.translationDirection || 'native-to-foreign',
          quizType: session.quizType || 'word-by-word'
        });
        setShowPracticeModal(true);
      } else {
        // New session - show config modal
        setShowConfigModal(true);
      }
    } catch (err) {
      console.error('Failed to start session:', err);
      notifyError(err.message || 'Failed to start practice session');
    }
  };

  const handleConfigComplete = async (config) => {
    try {
      // If not started yet, mark as in progress and save config
      if (currentSession.status === 'Not started') {
        const updatedSession = await startPracticeSession(
          currentSession._id,
          config.translationDirection,
          config.quizType
        );
        setCurrentSession(updatedSession);
      }
      setPracticeConfig(config);
      setShowConfigModal(false);
      setShowPracticeModal(true);
    } catch (err) {
      console.error('Failed to start session:', err);
      notifyError(err.message || 'Failed to start practice session');
    }
  };

  const handlePracticeComplete = () => {
    loadSessions();
  };

  const handleRestartSession = async (sessionId) => {
    setConfirmAction({
      type: 'restart',
      sessionId,
      message: 'This will create a new practice session with the same lectures. Continue?'
    });
  };

  const executeRestartSession = async (sessionId) => {
    try {
      setError(null);
      await restartPracticeSession(sessionId);
      await loadSessions();
      success('New practice session created!');
    } catch (err) {
      setError(err.message || 'Failed to restart practice session');
      notifyError(err.message || 'Failed to restart practice session');
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    if (confirmAction.type === 'delete') {
      await executeDeleteSession(confirmAction.sessionId);
    } else if (confirmAction.type === 'restart') {
      await executeRestartSession(confirmAction.sessionId);
    }

    setConfirmAction(null);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Practice Sessions</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          Add
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-600">Loading practice sessions...</div>
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600 mb-4">No practice sessions yet.</p>
          <p className="text-gray-500 text-sm">Click "Add" to create your first practice session.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sessions.map(session => {
            const firstLecture = session.lectures && session.lectures[0];
            const language = firstLecture?.schoolYear?.language;

            return (
              <div
                key={session._id}
                className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold">Session #{session.sessionNumber}</h3>
                      {language && (
                        <span className="px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full">
                          {language.name} ({language.nativeLanguage?.name || 'N/A'} → {language.foreignLanguage?.name || 'N/A'})
                        </span>
                      )}
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        session.status === 'Completed'
                          ? 'bg-green-100 text-green-800'
                          : session.status === 'In Progress'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {session.status}
                      </span>
                    </div>

                    <div className="mb-3 flex gap-4">
                      <span className="text-sm text-gray-600">
                        Overall Progress: <span className="font-semibold text-gray-900">{session.overallPercentage}%</span>
                      </span>
                      {session.status === 'Completed' && (
                        <span className="text-sm text-gray-600">
                          Final Score: <span className="font-semibold text-green-700">{session.finalScore}%</span>
                        </span>
                      )}
                    </div>

                    <div className="mb-2">
                      <span className="text-sm font-medium text-gray-700">Lectures ({session.lectures?.length || 0}):</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {session.lectures && session.lectures.map(lecture => (
                        <span
                          key={lecture._id}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-md"
                        >
                          {lecture.schoolYear?.name} - {lecture.name}
                        </span>
                      ))}
                    </div>

                    <div className="mt-3 text-xs text-gray-500">
                      Created: {new Date(session.createdAt).toLocaleDateString()}
                      {session.status === 'Completed' && session.updatedAt && (
                        <> • Completed: {new Date(session.updatedAt).toLocaleDateString()}</>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {session.status !== 'Completed' && (
                      <>
                        <button
                          onClick={() => handleStartPractice(session)}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          {session.status === 'In Progress' ? 'Proceed' : 'Start'}
                        </button>
                        <button
                          onClick={() => handleDeleteSession(session._id)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {session.status === 'Completed' && (
                      <button
                        onClick={() => handleRestartSession(session._id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Restart
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Practice Session Modal */}
      {showCreateModal && (
        <CreatePracticeSessionModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateSession}
          notify={{ success, error: notifyError, warning, info }}
        />
      )}

      {/* Practice Config Modal */}
      {showConfigModal && currentSession && (
        <PracticeConfigModal
          session={currentSession}
          onClose={() => {
            setShowConfigModal(false);
            setCurrentSession(null);
          }}
          onStart={handleConfigComplete}
        />
      )}

      {/* Practice Modal */}
      {showPracticeModal && currentSession && practiceConfig && (
        <PracticeModal
          session={currentSession}
          config={practiceConfig}
          onClose={() => {
            setShowPracticeModal(false);
            setCurrentSession(null);
            setPracticeConfig(null);
          }}
          onComplete={handlePracticeComplete}
          notify={{ success, error: notifyError, warning, info }}
        />
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm Action</h3>
            <p className="text-gray-700 mb-6">{confirmAction.message}</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                className={`px-4 py-2 text-white rounded-md ${
                  confirmAction.type === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {confirmAction.type === 'delete' ? 'Delete' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      {notifications.map(notif => (
        <Notification
          key={notif.id}
          message={notif.message}
          type={notif.type}
          duration={notif.duration}
          onClose={() => removeNotification(notif.id)}
        />
      ))}
    </div>
  );
};

export default PracticePage;
